import { createContext, useContext, useMemo, useState, useEffect } from 'react';
import { findIana } from 'windows-iana';
import { useLocation, useHistory } from 'react-router-dom';
import moment from 'moment';
import momentTz from 'moment-timezone';

import { UserDetailsContext } from 'context/userDetailsContext';
import { OutLookContext } from 'context/OutlookContext';

import { listContacts } from 'services/Contact/contactServices';
import { totalCountEvent } from 'services/generalServices';
import { getCalendarAttendees, listUsers } from 'services/users/userServices';
import { getOfficeLocations } from 'services/Settings';

import { getCalendarSettings, listEventTypes, listEvents } from 'services/Calender/calendarServices';
import { formatEventsOutLook, getUserWeekCalendar } from 'services/outLookServices';

import { generateFullName } from 'utils/generateFullNameUtils';
import { convertCaseToFilterFormat, formatMultiSelectContactList, getUserId, sortByKey } from 'utils/utils';

import { convertToTimezoneFormat, getTimezoneOffset } from 'utils/utility_functions/timezone';
import { useNavbarContext } from 'context/NavbarContext';

const CalendarContext = createContext({});

export const useCalendarContext = () => useContext(CalendarContext);

const getDateRangeInUserTimezone = (startDateStr, endDateStr, viewType, userTimezone) => {
  if (viewType === 'timeGridDay') {
    const selectedDate = momentTz.tz(startDateStr, userTimezone);
    return {
      date_filter_param: selectedDate.clone().startOf('day').format(),
      range_end_param: selectedDate.clone().endOf('day').format(),
    };
  }

  const start = momentTz.tz(startDateStr, userTimezone).startOf('day');
  const end = momentTz.tz(endDateStr, userTimezone).endOf('day');

  return {
    date_filter_param: start.format(),
    range_end_param: end.format(),
  };
};

export const CalendarContextProvider = (props) => {
  const { caseDetails, isFromCaseSpecificModule, isFromLeadSpecificModule, isClientCalendar, isGlobal, isClosed } = props;
  const location = useLocation();
  const history = useHistory();
  //Email Redirection logic
  const queryParams = new URLSearchParams(location?.search);
  const event_id = queryParams.get('id');

  const userContext = useContext(UserDetailsContext);
  const { readClientNotifications, notificationCounts } = useNavbarContext();
  const { user, displayError, authProvider, setUser } = useContext(OutLookContext);

  //lists
  const [locationsList, setLocationsList] = useState([]);
  const [officeLocationsList, setOfficeLocationsList] = useState([]);
  const [typeList, setTypeList] = useState([]);
  const [userList, setUserList] = useState();
  const [firmUserList, setFirmUserList] = useState([]);

  // event list
  const [eventList, setEventList] = useState([]);
  const [outlookEventList, setOutlookEventList] = useState([]);

  //filters
  const [calendarSettings, setCalendarSettings] = useState();
  const [calendarFilters, setCalendarFilters] = useState({
    locationFilter: isGlobal ? userContext?.firmDetails?.user_preferences?.filters?.calender?.by_location || '' : '',
    eventTypeFilter: isGlobal ? userContext?.firmDetails?.user_preferences?.filters?.calender?.event_type || '' : '',
    categoryFilter: isGlobal ? userContext?.firmDetails?.user_preferences?.filters?.calender?.by_category || '' : '',
    attendeesFilter: isGlobal ? userContext?.firmDetails?.user_preferences?.filters?.calender?.users || [getUserId()] : [],
    caseFilter:
      isGlobal && !isClientCalendar
        ? userContext?.firmDetails?.user_preferences?.filters?.calender?.case || ''
        : isClientCalendar
        ? convertCaseToFilterFormat(userContext?.clientSelectedCase)
        : '',
    // conference room filter (API level filtering like location)
    conferenceRoomFilter: isGlobal ? userContext?.firmDetails?.user_preferences?.filters?.calender?.conference_room || '' : '',
  });

  // local UI toggles not persisted in user settings
  const [showFirmEvents, setShowFirmEvents] = useState(false);

  //modals
  const [showCreateEventModal, setShowCreateEventModal] = useState(Boolean(location?.state?.createEvent));
  const [showEventPreviewModal, setShowEventPreviewModal] = useState(Boolean(event_id));
  const [editEventModal, setEditEventModal] = useState(false);
  const [editAllRecurringEvent, setEditAllRecurringEvent] = useState(false);
  const [showCalendarSettings, setShowCalendarSettings] = useState(false);

  const [selectedEvent, setSelectedEvent] = useState();
  const [uploadedAttachments, setUploadedAttachments] = useState([]);
  const [tempDeletedAttachments, setTempDeletedAttachments] = useState([]);
  const [showUserFilter, setShowUserFilter] = useState(false);
  const [showReminder, setShowReminder] = useState(true);

  //event details
  const [eventDetails, setEventDetails] = useState();

  const [currentView, setCurrentView] = useState('dayGridMonth');
  const [currentDates, setCurrentDates] = useState({
    currentStartValue: '',
    currentEndValue: '',
    currentStartDate: '',
    currentEndDate: '',
  });
  const [isLocationLoading, setIsLocationLoading] = useState(false);
  const [isEventTypeLoading, setIsEventTypeLoading] = useState(false);
  const [calendarDayClickedDetails, setCalendateDayClickedDetails] = useState({});

  const loadLocations = () => {
    setIsLocationLoading(true);
    totalCountEvent('contact', '', '', '', '', true)
      .then((response) => {
        let obj = {
          contactType: '',
          contactStatus: '',
          keyWord: '',
          is_court_location: true,
          is_event_location: true,
          pageLimit: response.data,
          excludeFromLoading: true,
        };

        listContacts(obj)
          .then((response) => {
            if (response && response.data && response.data.contacts && response.data.contacts.length > 0) {
              let options = response.data.contacts.map((index) => {
                return {
                  label: index.title ? index.title : index.office_name,
                  value: index.contact_id || index?.office_location_id,
                };
              });
              setLocationsList(options);
            }
          })
          .catch((err) => {
            console.log(err);
          })
          .finally(() => setIsLocationLoading(false));
      })
      .catch((err) => {
        setIsLocationLoading(false);
        console.log(err);
      });
  };

  const loadOfficeLocations = () => {
    getOfficeLocations()
      .then((response) => {
        if (response && response.data) {
          setOfficeLocationsList(response.data);
        }
      })
      .catch((err) => {
        console.log('Error loading office locations:', err);
      });
  };

  const loadTypes = () => {
    setIsEventTypeLoading(true);
    totalCountEvent('event-type', '', '', '', '', true)
      .then((response) => {
        let obj = {
          pageLimit: response.data,
          pagination_token: '',
          pagination_direction: '',
          excludeFromLoading: true,
        };
        listEventTypes(obj)
          .then((res) => {
            let data = res?.data['event-type'];
            let tempArray = [];

            data.forEach((item) => {
              tempArray.push({
                event_type: item.event_type,
                event_type_icon: item.event_type_icon,
                event_type_id: item.event_type_id,
              });
            });

            setTypeList(tempArray);
          })
          .catch((err) => {
            console.log(err);
          })
          .finally(() => setIsEventTypeLoading(false));
      })
      .catch((err) => {
        setIsEventTypeLoading(false);
        console.log(err);
      });
  };

  const fetchOutlookEvents = () => {
    if (calendarSettings?.sync_with_outlook && user) {
      getOutlookEvents();
    } else {
      setOutlookEventList([]);
    }
  };

  function getEventList() {
    let { currentStartDate, currentEndDate } = currentDates;

    const noDates = !currentStartDate && !currentEndDate;
    const noCase = isClientCalendar && !(caseDetails?.case_id || calendarFilters.caseFilter?.id);

    // Avoid premature fetch on case/lead-specific calendars before firm users are loaded
    const awaitingFirmUsers =
      (isFromCaseSpecificModule || isFromLeadSpecificModule) &&
      (!firmUserList || firmUserList.length === 0) &&
      Array.isArray(calendarFilters?.attendeesFilter) &&
      calendarFilters.attendeesFilter.length === 0;

    if (noDates || noCase || awaitingFirmUsers) return;

    const startDate = moment(currentStartDate).format('YYYY-MM-DD');
    const endDate = moment(currentEndDate).format('YYYY-MM-DD');

    const { date_filter_param, range_end_param } = getDateRangeInUserTimezone(
      startDate,
      endDate,
      currentView,
      userContext?.userDetails?.timezone
    );

    let obj = {
      event_type: calendarFilters.eventTypeFilter,
      // When showing firm-wide only, restrict to events category at API level
      category_filter: showFirmEvents ? 'event' : calendarFilters.categoryFilter,
      location: calendarFilters.locationFilter,
      conference_room: calendarFilters.conferenceRoomFilter,
      practice_area: calendarFilters.practiceAreaFilter,
      date_filter_param: date_filter_param,
      range_end_param: range_end_param,
      is_global_calendar: true,
      case_id: caseDetails?.case_id || calendarFilters.caseFilter?.id,
      next: '',
      user_timezone: getTimezoneOffset(userContext?.userDetails?.timezone),
      data: {
        firm_user: [...new Set(calendarFilters.attendeesFilter.flat())],
        get_calendar_items: calendarFilters?.attendeesFilter?.length === userList?.[0]?.items?.length + userList?.[1]?.items?.length,
        is_year_filter: currentView === 'multiMonthYear',
        // Firm-wide only switch from UI
        firm_wide_only: Boolean(showFirmEvents),
      },
    };
    listEvents(obj)
      .then((response) => {
        let events = response.data.events ? response.data.events : [];
        let cases = response.data.case ? response.data.case : [];
        let tasks = response.data.tasks ? response.data.tasks : [];
        let dob = response.data.dob ? response.data.dob : [];
        // Events (Meetings)
        if (events.length > 0) {
          events.forEach((item) => {
            const utcStart = item?.meeting_start_time;
            const utcEnd = item?.meeting_end_time;

            let start = momentTz.utc(utcStart).tz(userContext?.userDetails?.timezone);
            let end = momentTz.utc(utcEnd).tz(userContext?.userDetails?.timezone);
            const displayDate = momentTz.utc(currentStartDate).format('YYYY-MM-DD');

            if (item?.all_day) {
              item.start = start.format('YYYY-MM-DD');
              item.end = end.clone().add(1, 'day').format('YYYY-MM-DD');
            } else {
              item.start = start.format();
              item.end = end.format();
            }
            item.displayDate = displayDate;
            // normalize firm-wide flag from backend
            if (typeof item.firm_wide === 'undefined' && typeof item.is_firm_event !== 'undefined') {
              item.firm_wide = Boolean(item.is_firm_event);
            }
            // fallback: infer firm-wide from special accessible_list marker
            if (typeof item.firm_wide === 'undefined' || item.firm_wide === false) {
              if (Array.isArray(item?.accessible_list) && item.accessible_list.includes('__FIRM_WIDE__')) {
                item.firm_wide = true;
              }
            }
          });
        }
        // Cases (Statute of Limitations)
        if (cases.length > 0 && calendarSettings?.show_sol) {
          cases.forEach((item) => {
            const solDate = item?.important_dates?.statute_of_limitations;
            if (solDate) {
              let date = momentTz.utc(solDate).tz(userContext?.userDetails?.timezone);
              let end = date.clone().add(30, 'minutes'); // Ensure visible duration

              item.start = date.format();
              item.end = end.format();
              item.title = 'Statute Of Limitations';
              item.case_name = item.case_description?.case_name;
            }
          });
        }
        // Tasks
        if (tasks.length > 0) {
          tasks.forEach((item) => {
            const dueDate = item?.due_date;
            let date = momentTz.utc(dueDate).tz(userContext?.userDetails?.timezone);
            // Set fixed time 9:00 AM in that timezone
            let start = date.clone().hour(9).minute(0).second(0).millisecond(0);
            // Set fixed time 9:30 AM in that timezone
            let end = start.clone().add(30, 'minutes');

            item.start = start.format();
            item.end = end.format();
            item.title = item.task_name;
          });
        }
        if (dob.length > 0 && calendarSettings?.show_birthdays) {
          dob.forEach((item) => {
            let name = item.first_name || '';
            if (item.middle_name) name += ' ' + item.middle_name;
            if (item.last_name) name += ' ' + item.last_name;

            let originalDate = moment(item.dob);
            const currentYear = moment().year();
            originalDate.year(currentYear);

            let date = originalDate.tz(userContext?.userDetails?.timezone);
            let end = date.clone().add(30, 'minutes');

            item.start = date.format();
            item.end = end.format();
            item.title = `${name}'s birthday`;
          });
        }

        // Filter out firm-wide events for client calendars
        let data = [...events, ...cases, ...tasks, ...dob];
        if (isClientCalendar || userContext?.access_level === 'client') {
          data = data.filter((item) => !(item?.firm_wide === true));
        }

        // Apply conference room filter

        if (calendarFilters.conferenceRoomFilter) {
          data = data.filter((item) => {
            return item?.conference_room_id === calendarFilters.conferenceRoomFilter;
          });
        }

        let _eventList = data.filter((index) => index.start !== '');
        setEventList(_eventList);
      })
      .catch((err) => {
        console.log(err);
      });
  }

  async function getOutlookEvents() {
    if (user) {
      let { currentStartDate, currentEndDate } = currentDates;

      if (!currentStartDate && !currentEndDate) {
        return;
      }
      try {
        const ianaTimeZones = findIana(user?.timeZone);
        let events = await getUserWeekCalendar(authProvider, ianaTimeZones[0].valueOf(), currentStartDate, currentEndDate);
        setOutlookEventList(formatEventsOutLook(events));
      } catch (err) {
        displayError(err.message);
      }
    }
  }

  const getUserList = (selectedcase) => {
    // To get user list for filter user avatars
    if (isFromCaseSpecificModule || isFromLeadSpecificModule) {
      getCaseUserClientData(caseDetails?.case_id);
    } else if (selectedcase) {
      getCaseUserClientData(selectedcase);
      getFirmUsersListData();
    } else {
      getCaseUserClientData('');
      getFirmUsersListData();
    }
  };

  function getCaseUserClientData(case_id = '') {
    let obj = {
      caseId: case_id,
      all_contacts: case_id ? false : true,
      is_lead: isFromLeadSpecificModule ?? false,
      is_active: editEventModal ? false : true,
      active_groups: !editEventModal ? true : false,
    };
    getCalendarAttendees(obj)
      .then((response) => {
        let finalUserList = response?.data;

        if (editEventModal) {
          finalUserList = response?.data?.filter((user) => {
            const contactUserList =
              user.access_level === 'client' &&
              user?.status === 'ACTIVE' &&
              (user?.is_confirmed === true || user?.is_confirmed === undefined) &&
              user?.email;

            const activeContactUserList =
              user?.contact_id &&
              user?.status === 'ACTIVE' &&
              (user?.is_confirmed === true || user?.is_confirmed === undefined) &&
              user?.email;

            const eventContactUserList =
              user?.contact_id && eventDetails?.guests?.some((guest) => guest?.cognito_username === user?.client_cognito_username);

            const isActiveUser = user?.is_confirmed && user?.status === 'ACTIVE';
            const isArchivedConfirmed = user.res_type !== 'user-group' && (user.status === 'ARCHIVED' || user.is_confirmed === false);
            const isIndividualUser = eventDetails?.firm_users?.some(
              (firmUser) => firmUser?.is_individual_user && firmUser?.cognito_username === user?.client_cognito_username
            );

            const isActiveGroup = user.res_type === 'user-group' && user.status === 'ACTIVE';
            const isArchiveGroupList =
              user.res_type === 'user-group' && user?.status === 'ARCHIVED' && eventDetails.group_list.includes(user.user_group_id);

            return (
              contactUserList ||
              activeContactUserList ||
              eventContactUserList ||
              isActiveUser ||
              (isArchivedConfirmed && isIndividualUser) ||
              isActiveGroup ||
              isArchiveGroupList
            );
          });
        }

        let combinedData = combineUserAndGroup(finalUserList);
        setUserList(combinedData);
        // for avatar list
        if (isFromCaseSpecificModule || isFromLeadSpecificModule) {
          const responseData = response?.data;
          const filterDataWithoutGroups = responseData.filter((item) => item?.res_type !== 'user-group');
          setFirmUserList(ChangeCaseUserData(filterDataWithoutGroups));
        }
      })
      .catch((err) => console.error(err));
  }

  function combineUserAndGroup(response) {
    const groupedContacts = {};

    if (response?.length > 0) {
      response?.forEach((element) => {
        const contactType = element?.res_type === 'user-group' ? 'Groups' : !element.contact_type ? 'Firm Users' : 'Contacts';
        function generateLabel(element) {
          if (element.contact_type === 'person' || element.first_name || element.middle_name || element.last_name) {
            return generateFullName(element);
          }
          return element.title;
        }

        if (!groupedContacts[contactType]) {
          groupedContacts[contactType] = {
            label: contactType,
            items: [],
          };
        }

        if (element?.res_type === 'user-group') {
          groupedContacts[contactType].items.push({
            label: element.group_name,
            value: element.user_group_id,
            ...element,
          });
        } else if (element?.access_level) {
          groupedContacts[contactType]?.items.push({
            label: generateLabel(element),
            value: element.client_cognito_username ? element.client_cognito_username : element.contact_id,
            access_level: element.access_level,
            ...element,
          });
        } else {
          const label = generateLabel(element);
          groupedContacts[contactType].items.push({
            label: label,
            value: element.client_cognito_username ? element.client_cognito_username : element.contact_id,
            email: element.email,
            is_create_client_portal: element.is_create_client_portal,
            access_level: element.access_level,
            ...element,
          });
        }
      });
    }
    const groupedContactsSorted = groupedContacts['Firm Users']
      ? {
          ...groupedContacts,
          'Firm Users': groupedContacts['Firm Users']
            ? { ...groupedContacts['Firm Users'], items: sortByKey(groupedContacts['Firm Users']?.items, 'first_name') }
            : groupedContacts['Firm Users'],
        }
      : groupedContacts;
    return Object.values(groupedContactsSorted);
  }

  function getFirmUsersListData() {
    totalCountEvent('user')
      .then((response) => {
        let obj = {
          status: 'ACTIVE',
          limit: response.data ?? 10,
          pagination_token: '',
          pagination_direction: 'forward',
          keyWord: '',
          next: '',
          is_include_contacts: true,
          is_detailed: false,
          is_confirmed_user: true,
          is_include_groups: true,
        };
        listUsers(obj)
          .then((response) => {
            const formattedList = formatMultiSelectContactList(response.data?.firm_users);
            const sortedList = formattedList.map((item) =>
              item?.code === 'Users' ? { ...item, items: sortByKey(item.items, 'first_name') } : item
            );
            setFirmUserList(sortedList);
          })
          .catch((err) => console.error(err));
      })
      .catch((error) => console.warn(error));
  }

  const ChangeCaseUserData = (data) =>
    data.map((v) => ({
      ...v,
      full_name: generateFullName(v),
      user_id: v.client_cognito_username || v.contact_id,
    }));

  function hideCreateEventModal() {
    setShowCreateEventModal(false);
    const searchParams = new URLSearchParams(location.search);
    searchParams.delete('id');
    searchParams.delete('date');
    history.push({
      pathname: location.pathname,
      search: searchParams.toString(),
    });
    setSelectedEvent();
    setEventDetails();
    setEditEventModal();
    setUploadedAttachments();
    setShowUserFilter(false);
    setCalendateDayClickedDetails({});
  }

  function hidePreviewModal() {
    setShowEventPreviewModal(false);
    const searchParams = new URLSearchParams(location.search);
    searchParams.delete('id'); // remove only the one you want
    searchParams.delete('date');
    history.push({
      pathname: location.pathname,
      search: searchParams.toString(),
    });
    setSelectedEvent();
    setEventDetails();
    setEditEventModal();
    setUploadedAttachments();
  }

  const generateFirmUserIdList = () => {
    const userIdList = firmUserList?.map((user, idx) => user?.user_id);
    return userIdList;
  };

  const handleShowReminder = (show) => {
    if (!show) {
      setShowReminder(false);
    } else {
      setShowReminder(true);
    }
  };
  const calenderDayClick = (arg = {}, clicked = false) => {
    setShowCreateEventModal(clicked);
    setCalendateDayClickedDetails(arg);
  };

  useEffect(() => {
    setShowCreateEventModal(Boolean(location?.state?.createEvent));
  }, [location]);

  useEffect(() => {
    if (!Boolean(isClientCalendar)) {
      loadLocations();
      loadOfficeLocations();
      loadTypes();
    }
  }, []);

  useEffect(() => {
    if (showUserFilter && eventDetails?.case?.id) {
      getUserList(eventDetails?.case?.id);
    }
    if (showUserFilter && !eventDetails?.case?.id) {
      getUserList();
    }
    if (isFromCaseSpecificModule || isFromLeadSpecificModule) {
      getUserList();
    }
  }, [showUserFilter, isFromCaseSpecificModule, isFromLeadSpecificModule]);

  useEffect(() => {
    if (userContext?.userDetails?.contact_id && isClientCalendar && notificationCounts) {
      Boolean(notificationCounts?.event_count) && readClientNotifications('event', userContext.userDetails.contact_id);
    }
  }, [userContext?.userDetails?.contact_id, notificationCounts, isClientCalendar]);

  useEffect(() => {
    if (!isClientCalendar) {
      getCalendarSettings()
        .then((res) => {
          let data = res?.data?.calendar_settings;

          setCalendarSettings(data);

          if (!data?.sync_with_outlook) {
            setUser();
          }
        })
        .catch((err) => {
          console.log(err);
        });
    }
  }, []);

  // fetch calendar events
  useEffect(() => {
    if (currentDates) {
      getEventList();
    }
  }, [currentDates, calendarFilters, calendarSettings, showFirmEvents]);

  // fetch outlook events
  useEffect(() => {
    if (calendarSettings) {
      fetchOutlookEvents();
    }
  }, [calendarSettings, user, currentDates]);

  useEffect(() => {
    if (firmUserList?.length > 0 && (isFromCaseSpecificModule || isFromLeadSpecificModule)) {
      setCalendarFilters((prevValues) => {
        return {
          ...prevValues,
          attendeesFilter: [...generateFirmUserIdList()],
        };
      });
    }
  }, [firmUserList]);

  useEffect(() => {
    if (!userContext) return; // Wait until it's loaded

    setCalendarFilters({
      locationFilter: isGlobal ? userContext?.firmDetails?.user_preferences?.filters?.calender?.by_location || '' : '',
      eventTypeFilter: isGlobal ? userContext?.firmDetails?.user_preferences?.filters?.calender?.event_type || '' : '',
      categoryFilter: isGlobal ? userContext?.firmDetails?.user_preferences?.filters?.calender?.by_category || '' : '',
      attendeesFilter: isGlobal ? userContext?.firmDetails?.user_preferences?.filters?.calender?.users || [getUserId()] : [],
      caseFilter:
        isGlobal && !isClientCalendar
          ? userContext?.firmDetails?.user_preferences?.filters?.calender?.case || ''
          : isClientCalendar
          ? convertCaseToFilterFormat(userContext?.clientSelectedCase)
          : '',
      conferenceRoomFilter: isGlobal ? userContext?.firmDetails?.user_preferences?.filters?.calender?.conference_room || '' : '',
    });
  }, [userContext, isGlobal, isClientCalendar]);

  const contextPayload = useMemo(
    () => ({
      userContext,
      caseDetails,
      isFromCaseSpecificModule,
      isFromLeadSpecificModule,
      isClientCalendar,
      isGlobal,
      loadLocations,
      locationsList,
      officeLocationsList,
      typeList,
      // events
      eventList,
      setEventList,
      outlookEventList,
      setOutlookEventList,
      //modals
      showCreateEventModal,
      setShowCreateEventModal,
      loadTypes,
      showEventPreviewModal,
      setShowEventPreviewModal,
      selectedEvent,
      setSelectedEvent,
      editEventModal,
      setEditEventModal,
      editAllRecurringEvent,
      setEditAllRecurringEvent,
      getEventList,
      hideCreateEventModal,
      hidePreviewModal,
      uploadedAttachments,
      setUploadedAttachments,
      tempDeletedAttachments,
      setTempDeletedAttachments,
      currentView,
      setCurrentView,
      currentDates,
      setCurrentDates,
      //filters
      userList,
      setUserList,
      firmUserList,
      showCalendarSettings,
      setShowCalendarSettings,
      calendarSettings,
      setCalendarSettings,
      calendarFilters,
      setCalendarFilters,
      showFirmEvents,
      setShowFirmEvents,
      eventDetails,
      setEventDetails,
      setLocationsList,
      isClosed,
      generateFirmUserIdList,
      showUserFilter,
      setShowUserFilter,
      getUserList,
      showReminder,
      handleShowReminder,
      isLocationLoading,
      isEventTypeLoading,
      calenderDayClick,
      calendarDayClickedDetails,
      setCalendateDayClickedDetails,
    }),
    [
      userContext,
      locationsList,
      typeList,
      eventList,
      outlookEventList,
      showCreateEventModal,
      showEventPreviewModal,
      selectedEvent,
      editEventModal,
      editAllRecurringEvent,
      uploadedAttachments,
      tempDeletedAttachments,
      currentView,
      currentDates,
      userList,
      firmUserList,
      showCalendarSettings,
      calendarFilters,
      calendarSettings,
      eventDetails,
      isClosed,
      showUserFilter,
      setShowUserFilter,
      getUserList,
      showReminder,
      handleShowReminder,
      isLocationLoading,
      isEventTypeLoading,
      calenderDayClick,
      calendarDayClickedDetails,
      setCalendateDayClickedDetails,
    ]
  );

  return <CalendarContext.Provider value={contextPayload} {...props} />;
};
